import { DataStore } from 'aws-amplify';
import { Location, Printer, UserDetails } from '../models';
import { LocationProps, convertLocationToProps } from './locations';
import { PrinterProps, convertPrinterToProps } from './printers';

export interface UserDetailsProps {
  id: string;
  email?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  adminPanelAccess?: boolean | null;
  location?: LocationProps | null;
  printer?: PrinterProps | null;
}

interface UpdateAdminAccessParams {
  id: string;
  adminPanelAccess: boolean;
}

export interface UserDetailsDataProps {
  get: (id: string, cognitoUser: any) => Promise<UserDetailsProps>;
  getAll: () => Promise<UserDetailsProps[]>;
  update: (data: UserDetailsProps) => Promise<UserDetailsProps>;
  getOriginalUserDetails: (id: string) => Promise<UserDetails | undefined>;
  updateAdminPanelAccess: ({ id, adminPanelAccess }: UpdateAdminAccessParams) => Promise<UserDetailsProps>;
}

let createInFlight: Promise<UserDetails | null> | null = null;

export const userDetailsData = () => {
  async function get(cognitoID: string, cognitoUser: any): Promise<UserDetailsProps> {
    const details = await DataStore.query(UserDetails, (u) => u.cognitoID.eq(cognitoID));
    if (details.length) {
      const userDetails = details[0];
      if (userDetails) {
        const cognitoUserEmail = cognitoUser?.attributes?.email || null;
        const cognitoUserFirstname = cognitoUser?.attributes?.given_name || null;
        const cognitoUserLastname = cognitoUser?.attributes?.family_name || null;
        const convertedUser = await convertToProps(userDetails);

        if (
          (cognitoUserEmail && !userDetails.email) ||
          (cognitoUserFirstname && !userDetails.firstname) ||
          (cognitoUserLastname && !userDetails.lastname) ||
          (userDetails.email && cognitoUserEmail && userDetails.email !== cognitoUserEmail) ||
          (userDetails.firstname && cognitoUserFirstname && userDetails.firstname !== cognitoUserFirstname) ||
          (userDetails.lastname && cognitoUserLastname && userDetails.lastname !== cognitoUserLastname)
        ) {
          return await update({
            ...convertedUser,
            email: cognitoUserEmail,
            firstname: cognitoUserFirstname,
            lastname: cognitoUserLastname
          });
        }

        return convertedUser;
      }
    } else if (!createInFlight) {
      createInFlight = (async () => {
        const again = await DataStore.query(UserDetails, (u) => u.cognitoID.eq(cognitoID));

        if (again?.length) {
          return again[0];
        }

        return await DataStore.save(
          new UserDetails({
            cognitoID,
            email: cognitoUser?.attributes?.email ?? null,
            firstname: cognitoUser?.attributes?.given_name ?? null,
            lastname: cognitoUser?.attributes?.family_name ?? null,
            adminPanelAccess: false,
            location: null,
            printer: null
          })
        );
      })();

      const createdOrFound = await createInFlight;
      createInFlight = null;

      if (!createdOrFound) {
        return {} as UserDetailsProps;
      }
      return convertToProps(createdOrFound);
    }
    return {} as UserDetailsProps;
  }

  async function getAll() {
    const details = await DataStore.query(UserDetails);
    if (details.length) {
      const mappedData = await Promise.all(details.map((detail) => convertToProps(detail)));

      return mappedData;
    }
    return [];
  }

  async function update(data: UserDetailsProps) {
    const original = await getOriginalUserDetails(data.id);
    const locationToUpdate = data.location ? await DataStore.query(Location, data.location.id) : null;
    const printerToUpdate = data.printer ? await DataStore.query(Printer, data.printer.id) : null;
    if (original) {
      const updated = await DataStore.save(
        UserDetails.copyOf(original, (updated) => {
          updated.email = data.email;
          updated.firstname = data.firstname;
          updated.lastname = data.lastname;
          updated.adminPanelAccess = data.adminPanelAccess;
          updated.location = locationToUpdate;
          updated.printer = printerToUpdate;
        })
      );

      if (updated) {
        return await convertToProps(updated);
      }
    }
    return {} as UserDetailsProps;
  }

  async function updateAdminPanelAccess({ id, adminPanelAccess }: UpdateAdminAccessParams): Promise<UserDetailsProps> {
    const original = await getOriginalUserDetails(id);

    if (original) {
      const updated = await DataStore.save(
        UserDetails.copyOf(original, (updated) => {
          updated.adminPanelAccess = adminPanelAccess;
        })
      );

      if (updated) {
        return await convertToProps(updated);
      }
    }

    return {} as UserDetailsProps;
  }

  async function getOriginalUserDetails(id: string) {
    return await DataStore.query(UserDetails, id);
  }

  async function convertToProps(data: UserDetails) {
    const location = await data.location;
    const printer = await data.printer;

    const results: UserDetailsProps = {
      id: data.id,
      email: data.email,
      firstname: data.firstname,
      lastname: data.lastname,
      adminPanelAccess: data.adminPanelAccess,
      location: location ? await convertLocationToProps(location) : null,
      printer: printer ? convertPrinterToProps(printer) : null
    };

    return results;
  }

  const returned: UserDetailsDataProps = {
    get,
    getAll,
    update,
    getOriginalUserDetails,
    updateAdminPanelAccess
  };

  return returned;
};
